/* CARILOHA: Reactive photo grid for multipack bundles — replaces the standard product
   media gallery on the Boxer Bundle template. Renders one tile per pair using each color's
   real product photo, synced to the bundle configurator via a broadcast custom event
   ('cariloha:bundle-state') rather than any direct DOM coupling between the two components. */

import { Component } from '@theme/component';

/**
 * @typedef {Object} BmgColor
 * @property {string} label
 * @property {string|null} imageUrl
 */

/**
 * @typedef {Object} BmgRefs
 * @property {HTMLElement} root
 */

/** @extends {Component<BmgRefs>} */
export class BundleMediaGallery extends Component {
  /** @type {BmgColor[]} */
  #colors = [];

  /** @type {number} */
  #pairCount = 6;

  /** @type {string} */
  #colorPreset = 'mixed';

  /** @type {number|null} */
  #pct = 15;

  connectedCallback() {
    super.connectedCallback();

    const configEl = document.getElementById(this.dataset.configId);
    if (configEl) {
      try {
        const parsed = JSON.parse(configEl.textContent ?? '');
        this.#colors = (parsed.colors ?? []).filter((c) => c.imageUrl);
      } catch (e) {
        console.error('[bundle-media-gallery] Failed to parse config JSON:', e);
      }
    }

    document.addEventListener('cariloha:bundle-state', this.#handleBundleState);
    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('cariloha:bundle-state', this.#handleBundleState);
  }

  /** @param {CustomEvent<{pairCount: number, colorPreset: string, pct: number|null}>} event */
  #handleBundleState = (event) => {
    const { pairCount, colorPreset, pct } = event.detail ?? {};
    if (!pairCount || !colorPreset) return;
    if (pairCount === this.#pairCount && colorPreset === this.#colorPreset && pct === this.#pct) return;
    this.#pairCount = pairCount;
    this.#colorPreset = colorPreset;
    this.#pct = pct ?? null;
    this.#render();
  };

  /**
   * Which color tile a given pair index shows under the current preset. Mirrors
   * bundle-configurator.js's #presetProductFor: 'mixed' rotates through every color,
   * 'solid:{index}' pins every tile to that one color.
   * @param {number} idx
   * @returns {BmgColor|undefined}
   */
  #colorFor(idx) {
    if (!this.#colors.length) return undefined;
    if (this.#colorPreset === 'mixed') return this.#colors[idx % this.#colors.length];
    const match = /^solid:(\d+)$/.exec(this.#colorPreset ?? '');
    const solidIdx = match ? Number(match[1]) : 0;
    return this.#colors[solidIdx] ?? this.#colors[0];
  }

  #render() {
    const root = this.refs.root;
    if (!root || !this.#colors.length) return;

    const tiles = Array.from({ length: this.#pairCount }, (_, idx) => this.#colorFor(idx)).filter(Boolean);

    root.innerHTML = `
      ${this.#pct ? `<div class="bmg__badge">${this.#pairCount}-Pack &middot; Save ${this.#pct}%</div>` : ''}
      <div class="bmg__grid" style="--bmg-columns: 3;">
        ${tiles
          .map(
            (color, i) => `
              <div class="bmg__tile" style="animation-delay: ${Math.min(i, 8) * 40}ms">
                <img src="${this.#esc(color.imageUrl)}" alt="${this.#esc(color.label)}" loading="${i < 3 ? 'eager' : 'lazy'}">
              </div>
            `
          )
          .join('')}
      </div>
    `;
  }

  /**
   * @param {string} str
   * @returns {string}
   */
  #esc(str) {
    return String(str).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }
}

customElements.define('bundle-media-gallery', BundleMediaGallery);
